import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Input, Row, Col, Tag, Spin, Skeleton, Button, Tabs, Badge } from 'antd';
import {
  SearchOutlined, FireOutlined, ThunderboltOutlined, AppstoreOutlined,
  RightOutlined, ArrowRightOutlined, RocketOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ToolCard from '../components/ToolCard';
import CategoryIcon from '../components/CategoryIcon';
import Reveal from '../components/Reveal';
import RobotLogo from '../components/RobotLogo';
import { toolsAPI, categoriesAPI } from '../services/api';
import { Tool, Category } from '../types';
import { useLanguage } from '../store/LanguageContext';
import { hasStoredToolLogo } from '../utils/toolAssets';
import { getCategoryLabel } from '../utils/categoryLocale';

const { Search } = Input;

// 每个分类展示的工具数
const TOOLS_PER_CAT = 4;
// 首页展示的顶部分类数量
const TOP_CAT_COUNT = 8;

const JOB_LINKS_ZH = [
  { label: '写代码', path: '/category/ai-pair-programming' },
  { label: '出图', path: '/category/ai-text-to-image' },
  { label: '做视频', path: '/category/ai-short-video-maker' },
  { label: '写文案', path: '/task/write-ad-copy' },
  { label: '热门榜', path: '/rankings/popular' },
];

const JOB_LINKS_EN = [
  { label: 'Coding', path: '/category/ai-pair-programming' },
  { label: 'Images', path: '/category/ai-text-to-image' },
  { label: 'Video', path: '/category/ai-short-video-maker' },
  { label: 'Ad copy', path: '/task/write-ad-copy' },
  { label: 'Popular', path: '/rankings/popular' },
];

function formatCount(n: number, zh: boolean): string {
  if (!n) return '';
  // Huge catalog buckets look spammy; hide the vanity number.
  if (n >= 10000) return zh ? '可筛选' : 'Browse';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function logoQuality(tool: Tool): number {
  const url = String(tool.logo_url || tool.logo || tool.logo_source_url || '').toLowerCase();
  if (!url || !hasStoredToolLogo(tool)) return 0;
  if (url.endsWith('.webp') || url.endsWith('.png') || url.endsWith('.svg')) return 3;
  if (url.endsWith('.jpg') || url.endsWith('.jpeg')) return 2;
  if (url.endsWith('.ico')) return 1;
  return 2;
}

function isSpamFeaturedTool(tool: Tool): boolean {
  const url = String(tool.website_url || '').toLowerCase();
  const slug = String(tool.slug || '').toLowerCase();
  const name = String(tool.name || '');
  const blurb = `${tool.short_description || ''} ${tool.description || ''}`;
  if (url.includes('ai-bot.cn') || url.includes('topai.tools')) return true;
  if (slug.startsWith('cn-extra-') || slug.startsWith('awesome-') || slug.includes('glama-')) return true;
  if (/管理系统|协作平台|效率平台|创作工具|应用中心|分析引擎|学习工具/.test(name)) return true;
  if (/from curated AI list|curated AI list/i.test(blurb)) return true;
  return false;
}

function hasChineseBlurb(tool: Tool): boolean {
  const text = `${tool.description_zh || ''} ${tool.short_description || ''} ${tool.description || ''}`;
  return /[\u4e00-\u9fff]/.test(text) && !/from curated AI list/i.test(text);
}

function pickFeaturedTools(tools: Tool[]): Tool[] {
  const scored = [...tools]
    .filter((tool) => !!tool.website_url && !tool.asset_placeholder_logo && hasStoredToolLogo(tool) && !isSpamFeaturedTool(tool))
    .map((tool) => {
      let score = logoQuality(tool) * 3;
      if (tool.screenshot_urls?.length || tool.showcase_image) score += 2;
      if (tool.rating && Number(tool.rating) >= 4) score += 1;
      return { tool, score };
    });
  scored.sort((a, b) => b.score - a.score);
  return scored.map((x) => x.tool).slice(0, 12);
}

function pickSectionTools(tools: Tool[]): Tool[] {
  const scored = tools
    .filter((tool) => !!tool.website_url && !isSpamFeaturedTool(tool))
    .map((tool) => {
      let score = 0;
      if (hasStoredToolLogo(tool) && logoQuality(tool) >= 2) score += 4;
      if (hasChineseBlurb(tool)) score += 3;
      if (tool.rating && Number(tool.rating) >= 4) score += 1;
      return { tool, score };
    })
    .filter((x) => x.score >= 4);
  scored.sort((a, b) => b.score - a.score);
  const picked = scored.map((x) => x.tool).slice(0, TOOLS_PER_CAT);
  if (picked.length >= 2) return picked;
  return tools.filter((t) => !isSpamFeaturedTool(t)).slice(0, TOOLS_PER_CAT);
}

interface CategorySection {
  category: Category;
  tools: Tool[];
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const catNavRef = useRef<HTMLDivElement>(null);

  const [featuredTools, setFeaturedTools] = useState<Tool[]>([]);
  const [categorySections, setCategorySections] = useState<CategorySection[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCatTab, setActiveCatTab] = useState<string>('featured');
  const [searchValue, setSearchValue] = useState('');
  const [totalTools, setTotalTools] = useState(0);

  const categoryLabel = (cat: Category) => getCategoryLabel(cat, language);

  const sortedCategories = useMemo(
    () => [...allCategories]
      .filter((c) => (Number(c.tool_count) || 0) > 0)
      .sort((a, b) => (Number(b.tool_count) || 0) - (Number(a.tool_count) || 0)),
    [allCategories],
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [featuredRes, categoriesRes] = await Promise.all([
        toolsAPI.getFeatured(),
        categoriesAPI.getAll(),
      ]);

      const featured: Tool[] = pickFeaturedTools(featuredRes.data.tools || []);
      const cats: Category[] = categoriesRes.data.categories || [];
      setFeaturedTools(featured);
      setAllCategories(cats);
      setTotalTools(cats.reduce((s, c) => s + (Number(c.tool_count) || 0), 0));

      // Prefer curated / mid-size categories — huge dump buckets look like spam directories
      const preferredSlugs = [
        'ai-assistant', 'video-generation', 'image-generation', 'audio-voice',
        'code-assistant', 'productivity', 'design', 'writing-assistant',
      ];
      const bySlug = new Map(cats.map((c) => [c.slug, c]));
      const preferred = preferredSlugs.map((s) => bySlug.get(s)).filter(Boolean) as Category[];
      const fillers = [...cats]
        .filter((c) => {
          const n = Number(c.tool_count) || 0;
          return n > 0 && n < 8000 && !preferredSlugs.includes(c.slug);
        })
        .sort((a, b) => (Number(b.tool_count) || 0) - (Number(a.tool_count) || 0));
      const topCats = [...preferred, ...fillers].slice(0, TOP_CAT_COUNT);

      const results = await Promise.allSettled(
        topCats.map((cat) =>
          toolsAPI.getTools({ category: cat.id, limit: TOOLS_PER_CAT, sort: 'hot_score' })
        )
      );

      const sections: CategorySection[] = [];
      results.forEach((res, idx) => {
        if (res.status === 'fulfilled') {
          const tools = pickSectionTools(res.value.data.tools || []);
          if (tools.length > 0) {
            sections.push({ category: topCats[idx], tools });
          }
        }
      });
      setCategorySections(sections);
    } catch (error) {
      console.error('加载首页数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    if (value.trim()) {
      navigate(`/tools?search=${encodeURIComponent(value.trim())}`);
    }
  };

  const handleCategoryClick = (categoryId: number) => {
    const cat = sortedCategories.find((c) => c.id === categoryId);
    if (cat?.slug) {
      navigate(`/category/${encodeURIComponent(cat.slug)}`);
      return;
    }
    navigate(`/tools?category=${categoryId}`);
  };

  const catCountForHero = Math.min(8, sortedCategories.length || 8);

  return (
    <div>
      {/* ===== Hero：始终渲染，避免加载期空紫块 ===== */}
      <div className="hero-section">
        <h1 className="hero-title-row">
          <RobotLogo size={52} />
          <span>
            {language === 'zh-CN' ? (
              <>按任务选 <span className="hero-ai-highlight">AI</span> 工具</>
            ) : (
              <>Pick <span className="hero-ai-highlight">AI</span> tools by job</>
            )}
          </span>
        </h1>
        <p className="hero-sub">
          {t('home.heroSubWithCats', { toolCount: totalTools.toLocaleString(), catCount: String(catCountForHero) })}
        </p>

        <div className="hero-search">
          <Search
            placeholder={t('home.searchPlaceholder')}
            allowClear
            enterButton={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <SearchOutlined />
                {t('common.search')}
              </span>
            }
            size="large"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={handleSearch}
          />
        </div>

        <div className="hero-job-row" aria-label={t('home.jobsTitle')}>
          {(language === 'zh-CN' ? JOB_LINKS_ZH : JOB_LINKS_EN).map((job) => (
            <button
              key={job.path}
              type="button"
              className="hero-job-chip"
              onClick={() => navigate(job.path)}
            >
              {job.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== 分类导航 Tab 栏 ===== */}
      <div className="cat-nav-bar">
        <div className="cat-nav-inner" ref={catNavRef}>
          <button
            className={`cat-nav-chip ${activeCatTab === 'featured' ? 'active' : ''}`}
            onClick={() => setActiveCatTab('featured')}
          >
            <FireOutlined style={{ marginRight: 4 }} />
            {t('home.featured')}
          </button>

          {sortedCategories
            .filter((c) => (Number(c.tool_count) || 0) < 8000)
            .slice(0, 8)
            .map((cat) => (
            <button
              key={cat.id}
              className={`cat-nav-chip ${activeCatTab === String(cat.id) ? 'active' : ''}`}
              onClick={() => {
                setActiveCatTab(String(cat.id));
                handleCategoryClick(cat.id);
              }}
            >
              <CategoryIcon name={cat.icon} style={{ marginRight: 4 }} />
              {categoryLabel(cat)}
            </button>
          ))}

          <button
            className="cat-nav-chip cat-nav-more"
            onClick={() => navigate('/tools')}
          >
            <AppstoreOutlined style={{ marginRight: 4 }} />
            {t('home.allCategories')}
          </button>
        </div>
      </div>

      {/* ===== 主内容区 ===== */}
      <div className="page-container hp-main">
        {loading && (
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {Array(8).fill(0).map((_, i) => (
              <Col key={i} xs={24} sm={12} md={8} lg={6}>
                <Skeleton active avatar paragraph={{ rows: 3 }} />
              </Col>
            ))}
          </Row>
        )}

        {/* 精选推荐模块 */}
        {!loading && featuredTools.length > 0 && (
          <Reveal direction="up" duration={600}>
          <section className="hp-section">
            <div className="hp-section-header">
              <div className="hp-section-title">
                <FireOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                {t('home.featured')}
              </div>
              <button className="hp-view-all" onClick={() => navigate('/tools')}>
                {t('home.viewAll')}
                <ArrowRightOutlined style={{ marginLeft: 4 }} />
              </button>
            </div>
            <Row gutter={[16, 16]}>
              {featuredTools.map((tool, idx) => (
                <Col key={tool.id} xs={24} sm={12} md={8} lg={6}>
                  <Reveal delay={idx * 60} duration={500}>
                    <ToolCard tool={tool} />
                  </Reveal>
                </Col>
              ))}
            </Row>
          </section>
          </Reveal>
        )}

        {/* 按分类分组展示 */}
        {!loading && categorySections.map(({ category, tools }, sectionIdx) => (
          <Reveal key={category.id} direction="up" duration={600} delay={sectionIdx * 80}>
          <section className="hp-section">
            <div className="hp-section-header">
              <div className="hp-section-title">
                <CategoryIcon name={category.icon} style={{ marginRight: 8, fontSize: 20 }} />
                {categoryLabel(category)}
                {category.tool_count && Number(category.tool_count) < 10000 && (
                  <span className="hp-section-count">{t('home.toolCount', { count: String(category.tool_count) })}</span>
                )}
              </div>
              <button className="hp-view-all" onClick={() => handleCategoryClick(category.id)}>
                {t('home.viewAll')}
                <ArrowRightOutlined style={{ marginLeft: 4 }} />
              </button>
            </div>
            <Row gutter={[16, 16]}>
              {tools.map((tool, idx) => (
                <Col key={tool.id} xs={24} sm={12} md={8} lg={6}>
                  <Reveal delay={idx * 60} duration={500}>
                    <ToolCard tool={tool} />
                  </Reveal>
                </Col>
              ))}
            </Row>
          </section>
          </Reveal>
        ))}

        {/* ===== 所有分类卡片网格 ===== */}
        <Reveal direction="up" duration={600}>
        <section className="hp-section">
          <div className="hp-section-header">
            <div className="hp-section-title">
              <AppstoreOutlined style={{ color: '#722ed1', marginRight: 8 }} />
              {t('home.browseAllCategories')}
            </div>
            <button className="hp-view-all" onClick={() => navigate('/tools')}>
              {t('home.browseAll')}
              <ArrowRightOutlined style={{ marginLeft: 4 }} />
            </button>
          </div>
          <Row gutter={[12, 12]}>
            {sortedCategories.map((cat, idx) => (
              <Col key={cat.id} xs={12} sm={8} md={6} lg={4} xl={3}>
                <Reveal delay={idx * 30} duration={400}>
                <div
                  className="hp-cat-card"
                  onClick={() => handleCategoryClick(cat.id)}
                >
                  <div className="hp-cat-icon"><CategoryIcon name={cat.icon} /></div>
                  <div className="hp-cat-name">{categoryLabel(cat)}</div>
                  <div className="hp-cat-count">{formatCount(Number(cat.tool_count) || 0, language === 'zh-CN')}</div>
                </div>
                </Reveal>
              </Col>
            ))}
          </Row>
        </section>
        </Reveal>

        {/* ===== 提交工具 CTA 横幅 ===== */}
        <Reveal direction="up" duration={700}>
        <section
          style={{
            margin: '40px 0 20px',
            padding: '48px 32px',
            borderRadius: 16,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ position: 'relative', zIndex: 1 }}>
            <RocketOutlined style={{ fontSize: 40, color: '#fff', marginBottom: 12 }} />
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
              {t('submit.heroTitle')}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, marginBottom: 24, maxWidth: 500, margin: '0 auto 24px' }}>
              {t('submit.heroSubtitle')}
            </p>
            <Button
              type="primary"
              size="large"
              icon={<RocketOutlined />}
              onClick={() => navigate('/submit')}
              style={{
                background: '#fff',
                color: '#667eea',
                fontWeight: 600,
                border: 'none',
                height: 48,
                paddingInline: 32,
                fontSize: 16,
                borderRadius: 24,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              }}
            >
              {t('nav.submit') || 'Submit Tool'}
            </Button>
          </div>
        </section>
        </Reveal>
      </div>
    </div>
  );
};

export default HomePage;
